// Libraries
import { Box, Text } from '@chakra-ui/react';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
// Components
import { DropdownBlanket, DropdownButton } from '@molecules/index';
import { RatingsMenu } from './Components';
// Types
import { RatingsDropdownProps } from './RatingsDropdown.types';

/**
 * `RatingsDropdown` component provides a custom interface for a dropdown menu with rating filters.
 * It provides interactive features including opening and closing the dropdown menu, selecting/deselecting all options,
 * and closing the dropdown when the escape key is pressed or when the user clicks outside the dropdown.
 *
 * @component
 * @param {RatingsDropdownProps} props - The props to configure the RatingsDropdown component.
 * @param {ReactNode} props.children - Additional React elements to be displayed in the dropdown menu, typically a list of individual rating options.
 * @param {boolean} props.isPartiallyFiltered - Indicates whether some but not all rating options are selected.
 * @param {Function} props.handleSelectAll - Function to handle the "Select All" checkbox click, typically toggling selection of all rating options.
 * @param {string} props.message - The message to display in the dropdown button.
 * @param {boolean} props.w - Width of the dropdown element.
 *
 * @returns {ReactElement} The rendered RatingsDropdown component.
 */
export const RatingsDropdown = ({
  children,
  isPartiallyFiltered,
  handleSelectAll,
  message,
  w = 'full',
}: RatingsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleBlanketClick = () => {
    setIsOpen(false);
  };

  const escFunction = useCallback(({ key }: KeyboardEvent) => {
    if (key === 'Escape') {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  const childrenLength = children instanceof Array ? children.length : 1;
  const leftSize = childrenLength > 2 ? -25 : -5;

  return (
    <Box position='relative' w={w} minW={36}>
      <DropdownButton innerRef={buttonRef} isOpen={isOpen} setIsOpen={setIsOpen} isDisabled={false}>
        <Text fontSize='m' color='text.base'>
          {message}
        </Text>
      </DropdownButton>
      {isOpen && (
        <Fragment>
          <Box
            position='absolute'
            mt={2}
            left={leftSize}
            w='-webkit-fill-available'
            bg='white'
            zIndex='dropdown'
            border='greyCheck'
            borderRadius='md'
            boxShadow='dropdown'
            overflow='hidden'
          >
            <RatingsMenu
              isPartiallyFiltered={isPartiallyFiltered}
              handleSelectAll={handleSelectAll}
              usedInDropdown={true}
            >
              {children}
            </RatingsMenu>
          </Box>
          <DropdownBlanket onClick={handleBlanketClick} />
        </Fragment>
      )}
    </Box>
  );
};
